// components/HierarchySection.jsx
import React, { useMemo } from 'react';
import {
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Chip
} from '@mui/material';
import {
  Folder as FolderIcon,
  Flag as EpicIcon,
  BookOpen as StoryIcon,
  CheckSquare as TaskIcon,
  Bug as BugIcon
} from 'lucide-react';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Reusable dropdown component
const HierarchySelect = ({ id, label, value, onChange, options, Icon, loading, required = false }) => (
  <FormControl fullWidth sx={{ mb: 2 }}>
    <InputLabel id={`${id}-label`}>{label}</InputLabel>
    <Select
      labelId={`${id}-label`}
      id={id}
      value={value}
      onChange={onChange}
      label={label}
      required={required}
    >
      <MenuItem value="">
        <em>No {label} Selected</em>
      </MenuItem>
      {options.map(item => (
        <MenuItem key={item.id} value={item.id}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon size={16} />
            <Typography sx={{ ml: 1 }}>{item.summary || item.name}</Typography>
          </Box>
        </MenuItem>
      ))}
    </Select>
    {loading && (
      <Typography variant="caption" color="text.secondary">
        Loading {label.toLowerCase()}...
      </Typography>
    )}
  </FormControl>
);

const HierarchySection = ({
  selectedHierarchy,
  issueData,
  epics,
  stories,
  tasks,
  isEpicsLoading,
  isStoriesLoading,
  isTasksLoading,
  handleEpicChange,
  handleStoryChange,
  handleTaskChange,
  createUploadPath
}) => {
  const uploadPath = useMemo(() => createUploadPath(), [selectedHierarchy]);

  // Check if project is selected - either from selectedHierarchy or issueData
  const hasProject = selectedHierarchy?.project || issueData?.project_id;

  return (
    <Grid item xs={12}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Select Project Hierarchy Location:
        </Typography>  

        {/* Epic Selection - Show when project is selected */}
        {hasProject && (
          <HierarchySelect
            id="epic"
            label="Epic (Optional)"
            value={selectedHierarchy.epic?.id || ''}
            onChange={handleEpicChange}
            options={epics}
            Icon={EpicIcon}
            loading={isEpicsLoading}
          />
        )}  

        {/* Story Selection - Show when epic is selected AND issue type requires it */}
        {selectedHierarchy.epic &&
          (issueData.issueType === 3 || issueData.issueType === 4 || issueData.issueType === 1) && (
            <HierarchySelect
              id="story"
              label={
                issueData.issueType === 3
                  ? 'Story (This will be created under selected story)'
                  : 'Story (Required)'
              }
              value={selectedHierarchy.story?.id || ''}
              onChange={handleStoryChange}
              options={stories}
              Icon={StoryIcon}
              loading={isStoriesLoading}
              required={issueData.issueType === 4 || issueData.issueType === 1}
            />
          )}

        {/* Task Selection - Show when story is selected AND issue type requires it */}
        {selectedHierarchy.story &&
          (issueData.issueType === 4 || issueData.issueType === 1) && (
            <HierarchySelect
              id="task"
              label={
                issueData.issueType === 4
                  ? 'Task (This will be created under selected task)'
                  : 'Task (Required for Bug)'
              }
              value={selectedHierarchy.task?.id || ''}
              onChange={handleTaskChange}
              options={tasks}
              Icon={({ size }) =>
                selectedHierarchy.task?.type === 'Bug' ? (
                  <BugIcon size={size} />
                ) : (
                  <TaskIcon size={size} />
                )
              }
              loading={isTasksLoading}
              required={issueData.issueType === 1}
            />
          )}

        {/* Breadcrumb for Selection Path */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Selected Path:
          </Typography>
          <Breadcrumbs sx={{ mt: 0.5 }}>
            <Chip
              icon={<FolderIcon size={16} />}
              label={selectedHierarchy.project?.name || issueData.projectName || 'No Project'}
              size="small"
              color={(selectedHierarchy.project || issueData.project_id) ? 'primary' : 'default'}
            />
            {selectedHierarchy.epic && (
              <Chip
                icon={<EpicIcon size={16} />}
                label={selectedHierarchy.epic.summary || selectedHierarchy.epic.name}
                size="small"
                color="secondary"
              />
            )}
            {selectedHierarchy.story && (
              <Chip
                icon={<StoryIcon size={16} />}
                label={selectedHierarchy.story.summary || selectedHierarchy.story.name}
                size="small"
                color="info"
              />
            )}
            {selectedHierarchy.task && (
              <Chip
                icon={
                  selectedHierarchy.task.type === 'Bug' ? (
                    <BugIcon size={16} />
                  ) : (
                    <TaskIcon size={16} />
                  )
                }
                label={selectedHierarchy.task.summary || selectedHierarchy.task.name}
                size="small"
                color="warning"
              />
            )}
            {issueData.issueType === 1 && selectedHierarchy.task && (
              <Chip icon={<BugIcon size={16} />} label="Bug" size="small" color="error" />
            )}
          </Breadcrumbs>
        </Box>

        {/* Upload Path Display */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload path: <code>{uploadPath}</code>
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

export default HierarchySection;